import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { AgxButton, AgxSpacer, AgxTypography, Icon_Arrow } from '@urbanx/agx.ui-components2';
import {
  IdentityMigrationStatusResponseStatus as Status,
  useGetIdentityMigration,
  useInvalidateUseGetIdentityMigration,
  useResumeIdentityMigration,
  useRerunIdentityMigration,
} from '@urbanx/agx.support.client';
import dayjs from 'dayjs';
import Panel from 'components/panel/Panel';
import { identityMigrationStatusLabels } from './identityMigrationStatusLabels';

function message(failure: unknown, fallback: string) {
  return typeof failure === 'string'
    ? failure
    : failure instanceof Error
      ? failure.message
      : fallback;
}

export default function IdentityMigrationStatus() {
  const { operationId = '' } = useParams();
  const { data, isError, error } = useGetIdentityMigration(operationId);
  const invalidate = useInvalidateUseGetIdentityMigration();
  const { resumeIdentityMigration, resumeIdentityMigrationPending } = useResumeIdentityMigration();
  const { rerunIdentityMigration, rerunIdentityMigrationPending } = useRerunIdentityMigration();
  const [actionError, setActionError] = useState('');
  const inProgress = data?.status === Status.InProgress;
  useEffect(() => {
    if (!inProgress) return;
    const timer = window.setInterval(() => void invalidate(operationId), 2000);
    return () => window.clearInterval(timer);
  }, [inProgress, operationId, invalidate]);
  async function act(action: () => Promise<unknown>) {
    setActionError('');
    try {
      await action();
      await invalidate(operationId);
    } catch (failure) {
      setActionError(message(failure, 'Unable to complete this migration action.'));
    }
  }

  return (
    <AgxSpacer gap="spacing-lg" fill>
      <AgxButton
        id="identity-migration-back"
        text="Identity migrations"
        variant="naked"
        size="small"
        leftIcon={<Icon_Arrow direction="left" width={16} height={16} />}
        renderLink={(props) => <Link to="..?tab=history" {...props} />}
      />
      <Panel title="Identity migration">
        <section aria-label="Identity migration">
          <AgxSpacer gap="spacing-lg" fill>
            {isError && (
              <AgxTypography type="form_error" role="alert">
                {message(error, 'Unable to load this migration.')}
              </AgxTypography>
            )}
            {data && (
              <>
                <AgxTypography type="body_ui_strong" size="medium" role="status">
                  {identityMigrationStatusLabels[data.status]}
                </AgxTypography>
                {inProgress && (
                  <AgxTypography type="body" size="medium">
                    {data.completedSteps} of {data.totalSteps} records moved
                  </AgxTypography>
                )}
                {data.error && (
                  <AgxTypography type="form_error" role="alert">
                    {data.error}
                  </AgxTypography>
                )}
                <AgxTypography type="body" size="medium">
                  {data.sourceEmail} → {data.destinationEmail}
                </AgxTypography>
                <AgxTypography type="body" size="medium">
                  Reason: {data.reason}
                </AgxTypography>
                <AgxTypography type="body" size="medium">
                  Started by {data.operatorId} on{' '}
                  {dayjs(data.startedUtc).format('DD/MM/YYYY h:mm A')}
                </AgxTypography>
                {data.status === Status.Error && (
                  <AgxButton
                    id="identity-migration-resume"
                    variant="primary"
                    text={resumeIdentityMigrationPending ? 'Resuming…' : 'Resume migration'}
                    disabled={resumeIdentityMigrationPending}
                    onClick={() => void act(() => resumeIdentityMigration({ operationId }))}
                  />
                )}
                {data.status === Status.Complete && (
                  <AgxButton
                    id="identity-migration-rerun"
                    variant="hollow"
                    text={rerunIdentityMigrationPending ? 'Rerunning…' : 'Rerun migration'}
                    disabled={rerunIdentityMigrationPending}
                    onClick={() => void act(() => rerunIdentityMigration({ operationId }))}
                  />
                )}
              </>
            )}
            {actionError && (
              <AgxTypography type="form_error" role="alert">
                {actionError}
              </AgxTypography>
            )}
          </AgxSpacer>
        </section>
      </Panel>
    </AgxSpacer>
  );
}
